import React, {memo, useEffect, useRef, useState} from "react";
import {ChevronLeft, ChevronRight, LayoutGrid} from "lucide-react";

// Grille produits et barre de catégories de la caisse web — même dessin que
// la caisse desktop (electron-pos ProductGrid.jsx / FilterCard.jsx) : tuiles
// à plat séparées par des filets partagés, image en haut, bandeau prix teinté
// en bas, anneau intérieur teal au survol / sur la catégorie active.

const FALLBACK_COLORS = ["#4CAF50", "#FF6B9D", "#FF7675", "#4ECDC4", "#FFD93D", "#A29BFE", "#74B9FF", "#FD79A8", "#FDCB6E", "#55EFC4"];
const colorFor = (item) => item.color || FALLBACK_COLORS[((item.name || "").charCodeAt(0) || 0) % FALLBACK_COLORS.length];

// « Beautiful Color » → BC ; « Coca » → CO.
export const nameInitials = (name) => {
    const words = String(name || "").trim().split(/\s+/).filter(Boolean);
    if (words.length >= 2) return (words[0][0] + words[1][0]).toUpperCase();
    return (words[0] || "?").slice(0, 2).toUpperCase();
};

// Image si elle se charge, sinon initiales sur la couleur.
export const Thumb = ({image, color, label, className = "", initialsClass = "text-sm"}) => {
    const [failed, setFailed] = useState(false);
    useEffect(() => setFailed(false), [image]);
    if (image && !failed) {
        return <img src={image} alt="" loading="lazy" draggable={false} onError={() => setFailed(true)}
                    className={`${className} object-cover bg-gray-100 dark:bg-gray-700 pointer-events-none`}/>;
    }
    return (
        <span className={`${className} ${initialsClass} flex items-center justify-center text-white/95 font-extrabold tracking-wide pointer-events-none`}
              style={{background: color || "#4DB6AC"}}>
            {nameInitials(label)}
        </span>
    );
};

// ── Catégories ──────────────────────────────────────────────────────────────

const CategoryTile = ({item, active, onClick}) => {
    const isAll = item.id === "all";
    const color = isAll ? "#4DB6AC" : colorFor(item);
    return (
        <button type="button" onClick={onClick}
                className="relative w-[88px] h-[84px] flex-shrink-0 flex flex-col bg-white dark:bg-gray-800 border-r border-b border-gray-200 dark:border-gray-700 overflow-hidden transition-shadow select-none"
                style={{boxShadow: active ? `inset 0 0 0 2px ${color}` : undefined}}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.boxShadow = `inset 0 0 0 2px ${color}`; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.boxShadow = ""; }}>
            <span className="h-[52px] w-full flex items-center justify-center overflow-hidden" style={{background: color}}>
                {isAll
                    ? <LayoutGrid className="w-6 h-6 text-white/95"/>
                    : <Thumb image={item.image} color={color} label={item.name} className="w-full h-full" initialsClass="text-lg"/>}
            </span>
            <span className={`flex-1 flex items-center justify-center px-1 text-[12px] leading-tight text-center line-clamp-2 ${
                active ? "font-bold" : "font-semibold text-gray-500 dark:text-gray-400"}`}
                  style={active ? {color} : undefined}>
                {item.name}
            </span>
        </button>
    );
};

const ScrollArrow = ({dir, onClick}) => (
    <button type="button" onClick={onClick} aria-label={dir === "left" ? "Précédent" : "Suivant"}
            className={`w-6 flex-shrink-0 flex items-center justify-center bg-white dark:bg-gray-800 text-gray-400 hover:text-primary border-gray-200 dark:border-gray-700 ${
                dir === "left" ? "border-r" : "border-l"}`}>
        {dir === "left" ? <ChevronLeft className="w-4 h-4" strokeWidth={2.5}/> : <ChevronRight className="w-4 h-4" strokeWidth={2.5}/>}
    </button>
);

export const CategoryBar = ({categories, activeId, onSelect}) => {
    const scrollRef = useRef(null);
    const scroll = (dir) => scrollRef.current?.scrollBy({left: dir * 300, behavior: "smooth"});
    return (
        <div className="relative z-[2] flex items-stretch flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-[0_4px_12px_rgba(0,0,0,0.08)]">
            <ScrollArrow dir="left" onClick={() => scroll(-1)}/>
            <div ref={scrollRef}
                 onWheel={(e) => { if (e.deltaY !== 0) e.currentTarget.scrollLeft += e.deltaY; }}
                 className="flex flex-1 overflow-x-auto border-t border-gray-200 dark:border-gray-700 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {[{id: "all", name: "Toutes"}, ...categories].map((c) => (
                    <CategoryTile key={c.id} item={c} active={activeId === c.id} onClick={() => onSelect(c.id)}/>
                ))}
            </div>
            <ScrollArrow dir="right" onClick={() => scroll(1)}/>
        </div>
    );
};

// ── Produits ────────────────────────────────────────────────────────────────

const ProductCard = memo(function ProductCard({product, fallbackImage, fallbackColor, onClick}) {
    const hasVariants = product.variants.length > 0;
    return (
        <button type="button" onClick={() => onClick(product)} title={product.name}
                className="group relative flex flex-col items-stretch text-center bg-white dark:bg-gray-800 border-r border-b border-gray-300/60 dark:border-gray-600/60 transition-[background,box-shadow] hover:shadow-[inset_0_0_0_2px_#4DB6AC] active:bg-primary/10 select-none">
            {hasVariants && (
                <span className="absolute top-1 right-1 z-[1] px-1.5 py-[2px] rounded-full bg-primary text-white text-[7px] font-extrabold tracking-wide">
                    VARIANTES
                </span>
            )}
            <Thumb image={product.image || fallbackImage} color={product.color || fallbackColor} label={product.name}
                   className="w-full h-[58px]" initialsClass="text-[17px]"/>
            <span className="mx-1.5 mt-1 mb-1 text-[12px] leading-[1.25] font-semibold text-gray-900 dark:text-white line-clamp-2 min-h-[30px] break-words">
                {product.name}
            </span>
            <span className="mt-auto py-1 px-2 bg-primary/10 text-primary text-[13px] font-extrabold tabular-nums">
                {hasVariants
                    ? `${product.variants.length} choix`
                    : <>{(Number(product.price) || 0).toFixed(2)} <span className="text-[10px] font-bold">DH</span></>}
            </span>
        </button>
    );
});

export const ProductGrid = ({products, categoriesById, onPick}) => {
    if (!products.length) {
        return <div className="flex-1 flex items-center justify-center text-sm text-gray-500 dark:text-gray-400">Aucun produit</div>;
    }
    return (
        <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain p-2">
            <div className="grid grid-cols-[repeat(auto-fill,minmax(100px,1fr))] border-t border-l border-gray-300/60 dark:border-gray-600/60">
                {products.map((p) => {
                    const cat = categoriesById.get(p.categoryId);
                    return (
                        <ProductCard key={p.id} product={p} onClick={onPick}
                                     fallbackImage={cat?.image} fallbackColor={cat?.color}/>
                    );
                })}
            </div>
        </div>
    );
};
